import type { CaseSeries, ImageLabelSchema } from "@frontend/pages/case-workspace/radiology/types"
import type { ImageLabelAnnotationState } from "@shared/lib/history-types"
import { Check, ChevronDown } from "lucide-react"
import { useRef, useState } from "react"

interface ImageLabelModulePanelProps {
  schema: ImageLabelSchema
  moduleId: string
  seriesId: string | null
  imageLabels: ImageLabelAnnotationState[]
  onUpsertImageLabel: (label: ImageLabelAnnotationState) => void
  availableSeries?: CaseSeries[]
}

export function ImageLabelModulePanel({
  schema,
  moduleId,
  seriesId,
  imageLabels,
  onUpsertImageLabel,
  availableSeries = []
}: ImageLabelModulePanelProps) {
  const [generatedId] = useState(() => crypto.randomUUID())
  const existing = imageLabels.find((l) => l.projectModuleId === moduleId && l.seriesId === seriesId)
  const currentId = existing?.id ?? generatedId

  const seriesMappingEnabled = schema.series_mapping && availableSeries.length > 0

  function emitUpdate(nextLabels: Record<string, boolean>, nextSeriesMapping?: Record<string, string[]>) {
    onUpsertImageLabel({
      id: currentId,
      seriesId,
      projectModuleId: moduleId,
      labels: nextLabels,
      ...(seriesMappingEnabled ? { seriesMapping: nextSeriesMapping } : {})
    })
  }

  function toggleOption(value: string) {
    const current = existing?.labels ?? {}
    const currentMapping = existing?.seriesMapping ?? {}

    const nextLabels = schema.multi_select
      ? { ...current, [value]: !current[value] }
      : Object.fromEntries(schema.options.map((o) => [o.value, o.value === value ? !current[value] : false]))

    // When un-checking a label, clear its series mapping
    const nextMapping = { ...currentMapping }
    if (!nextLabels[value]) {
      delete nextMapping[value]
    }
    // For single-select, clear mappings for other labels that got deselected
    if (!schema.multi_select) {
      for (const key of Object.keys(nextMapping)) {
        if (!nextLabels[key]) delete nextMapping[key]
      }
    }

    emitUpdate(nextLabels, nextMapping)
  }

  function updateSeriesMapping(labelValue: string, seriesIds: string[]) {
    const currentLabels = existing?.labels ?? {}
    const currentMapping = existing?.seriesMapping ?? {}
    const nextMapping = { ...currentMapping, [labelValue]: seriesIds }
    emitUpdate(currentLabels, nextMapping)
  }

  return (
    <div className="flex-1 overflow-auto p-3">
      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
        {schema.multi_select ? "Select all that apply" : "Select one"}
      </div>
      <div className="space-y-1">
        {schema.options.map((opt) => {
          const selected = existing?.labels[opt.value] ?? false
          const mappedSeries = existing?.seriesMapping?.[opt.value] ?? []

          return (
            <div
              key={opt.value}
              className={`flex items-center gap-2 px-2.5 py-2 rounded-md border transition-colors ${
                selected ? "border-primary/60 bg-primary/10" : "border-border bg-surface-raised hover:bg-accent/40"
              }`}
            >
              {/* Checkbox + label — click to toggle */}
              <button className="flex items-center gap-2.5 min-w-0 shrink-0" onClick={() => toggleOption(opt.value)}>
                <span
                  className={`size-3.5 rounded-sm shrink-0 border flex items-center justify-center ${
                    selected ? "bg-primary border-primary" : "border-border"
                  }`}
                >
                  {selected && <Check className="size-2.5 text-primary-foreground" />}
                </span>
                <span className="text-[11px] font-medium whitespace-nowrap">{opt.label}</span>
              </button>

              {/* Series multi-select — inline, right-aligned */}
              {seriesMappingEnabled && selected && (
                <InlineSeriesSelect
                  className="ml-auto"
                  availableSeries={availableSeries}
                  selectedIds={mappedSeries}
                  onChange={(ids) => updateSeriesMapping(opt.value, ids)}
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Compact inline multi-select for series (right side of row)
// ---------------------------------------------------------------------------

function InlineSeriesSelect({
  availableSeries,
  selectedIds,
  onChange,
  className
}: {
  availableSeries: CaseSeries[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Filter out SEG series — they're overlays, not source images
  const selectableSeries = availableSeries.filter((s) => s.image_type !== "SEG")

  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  function getLabel(s: CaseSeries) {
    return s.series_description || s.series_identifier
  }

  const count = selectedIds.length
  const triggerLabel =
    count === 0
      ? "Series…"
      : count === 1
        ? getLabel(availableSeries.find((s) => s.id === selectedIds[0])!)
        : `${count} series`

  return (
    <div
      ref={containerRef}
      className={`relative ${className ?? ""}`}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
          setOpen(false)
        }
      }}
    >
      {/* Trigger — compact single button that shows count or name */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] leading-tight border transition-colors max-w-[120px] ${
          count > 0
            ? "bg-primary/15 text-primary border-primary/30 hover:bg-primary/25"
            : "text-muted-foreground border-dashed border-border hover:text-foreground hover:border-muted-foreground"
        }`}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDown className={`size-2.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 right-0 top-full mt-1 w-56 max-h-48 overflow-auto rounded-md border border-border bg-popover shadow-md">
          {selectableSeries.length === 0 ? (
            <div className="p-2 text-[10px] text-muted-foreground">No series available</div>
          ) : (
            selectableSeries.map((s) => {
              const checked = selectedIds.includes(s.id)
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.id)}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-accent/40 transition-colors"
                >
                  <span
                    className={`size-3 rounded-sm shrink-0 border flex items-center justify-center ${
                      checked ? "bg-primary border-primary" : "border-border"
                    }`}
                  >
                    {checked && <Check className="size-2 text-primary-foreground" />}
                  </span>
                  <span className="text-[10px] truncate">{getLabel(s)}</span>
                  {s.modality && (
                    <span className="ml-auto text-[9px] text-muted-foreground shrink-0">{s.modality}</span>
                  )}
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
